import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation, Trans } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LaunchIcon from '@mui/icons-material/Launch';
import EmailIcon from '@mui/icons-material/Email';
import LockResetIcon from '@mui/icons-material/LockReset';
import BlockIcon from '@mui/icons-material/Block';
import TimerOffIcon from '@mui/icons-material/TimerOff';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import UpdateIcon from '@mui/icons-material/Update';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PaymentsIcon from '@mui/icons-material/Payments';
import TuneIcon from '@mui/icons-material/Tune';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DeleteIcon from '@mui/icons-material/Delete';
import { adminService } from '../../services/admin';
import {
  AGENCY_XS_MAX_BRANDS,
  TARGET_LIMITS,
  TIER_LIMITS,
  effectiveFeatureFlags,
  isAgencyXsAllocation,
  isTrialExpired,
  type BillingStatus,
  type FeatureFlags,
  type QuotaOverrides,
  type Tenant,
  type Tier,
  type User,
} from '@traaker/shared';

// Tiers eligible for the admin "change trial tier" action — mirrors
// TRIAL_ELIGIBLE_TIERS in admin.service.ts (agency/enterprise excluded: they
// carry contract/credit metadata a bare tier flip won't set).
const TRIAL_ELIGIBLE_TIERS: Tier[] = ['basic', 'pro', 'elite'];

const BILLING_COLORS: Record<BillingStatus, string> = {
  pending: '#8B9DC3',
  trialing: '#00DDB3',
  active: '#00DDB3',
  past_due: '#F59E0B',
  suspended: '#EF4444',
  cancelled: '#6B7280',
};

const QUOTA_KEYS: Array<keyof QuotaOverrides> = [
  'brands',
  'zones',
  'users',
  'competitors',
  'pagesPerAudit',
  'pagesPerDomain',
  'apiCallsPerDay',
  'concurrentAgents',
  'socialIntegrations',
  'prompts',
  'brandNarrativeRunsPerMonth',
  'brandAuthorityRunsPerMonth',
  'brandReferralRunsPerMonth',
  // Product module — both default to 0 on every non-agency tier, so setting
  // these two IS what unlocks the module's capacity for an enterprise deal
  // (alongside the productModuleEnabled flag).
  'catalog_items',
  'product_reviews',
  // Cibles — establishments is tenant-wide (Geoapify geocoding budget, default 100).
  // target_signals is per-brand (search Lambda memory cap, default 500k). D13, D34.
  'establishments',
  'target_signals',
  // Conversations — 0 on basic/pro/elite by default (the flag is ON everywhere); > 0 grants access.
  'conversation_personas',
];

// Boolean-valued quota overrides — rendered as a 3-state (default/on/off)
// select rather than a number input. Stored in the same string-keyed
// `quotaOverrides` dialog state as '' (unset, use tier default) / 'true' / 'false'.
const BOOLEAN_QUOTA_KEYS: Array<keyof QuotaOverrides> = ['webhookAccess'];

// BP engines (run-path ids). Secondary = simulated (a model + a web-search
// harness we own).
const ENGINE_OPTIONS: Array<{ id: string; label: string; secondary?: boolean }> = [
  { id: 'perplexity', label: 'Perplexity' },
  { id: 'openai', label: 'ChatGPT' },
  { id: 'gemini', label: 'Gemini' },
  { id: 'claude', label: 'Claude' },
  { id: 'mistral', label: 'Mistral', secondary: true },
  { id: 'ai_overview', label: 'AI Overview', secondary: true },
  { id: 'google_ai_mode', label: 'Google AI Mode', secondary: true },
  // Conversations only — no BP engine has this id. Listed because an `engines`
  // override REPLACES the allowlist: omit it here and saving an override silently
  // rejects every persona carrying a `google` cell.
  { id: 'google', label: 'Google AI Overview (conversations)', secondary: true },
];
const DEFAULT_ENGINE_IDS = ENGINE_OPTIONS.filter((e) => !e.secondary).map((e) => e.id);

const FLAG_LABELS: Array<{ key: keyof FeatureFlags; labelKey: string }> = [
  { key: 'tasksEnabled', labelKey: 'admin.detail.flag.tasks' },
  { key: 'newsWatchEnabled', labelKey: 'admin.detail.flag.newsWatch' },
  { key: 'competitorsEnabled', labelKey: 'admin.detail.flag.competitors' },
  { key: 'reportsEnabled', labelKey: 'admin.detail.flag.reports' },
  { key: 'productModuleEnabled', labelKey: 'admin.detail.flag.productModule' },
  { key: 'conversationsEnabled', labelKey: 'admin.detail.flag.conversations' },
];

// Agency-tier-only flags: surface a normally admin-only agency plan (Agency
// XS, Agency Démarrage) on the tenant's own billing page. Kept separate from
// FLAG_LABELS so they never render for non-agency tenants (these offers are
// meaningless outside agency).
const AGENCY_ONLY_FLAGS: Array<{ key: keyof FeatureFlags; labelKey: string }> = [
  { key: 'agencyXsOffer', labelKey: 'admin.detail.flag.agencyXsOffer' },
  { key: 'agencyDemarrageOffer', labelKey: 'admin.detail.flag.agencyDemarrageOffer' },
];

type CreditBalance = { subscriptionCredits: number; topupCredits: number; reserved: number; available: number };

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={600}>{value ?? '—'}</Typography>
    </Box>
  );
}

/**
 * Read-only tenant detail. Every mutation is a discrete, confirmed action (modal),
 * so an admin can't casually break a live tenant by editing a free-for-all form.
 */
export default function AdminTenantDetail() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const isAdmin = !!currentUser?.isPlatformAdmin;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [creditBalance, setCreditBalance] = useState<CreditBalance | null>(null);

  // Which action dialog is open.
  const [dialog, setDialog] = useState<
    | null
    | 'credits'
    | 'suspend'
    | 'end-trial'
    | 'extend-trial'
    | 'trial-tier'
    | 'reactivate'
    | 'reset'
    | 'quotas'
    | 'contract'
    | 'delete'
  >(null);
  const [busy, setBusy] = useState(false);

  // Action-local form state.
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [resetEmail, setResetEmail] = useState<string>('');
  const [quotaOverrides, setQuotaOverrides] = useState<Record<string, string>>({});
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>({});
  const [enabledEngines, setEnabledEngines] = useState<string[]>(DEFAULT_ENGINE_IDS);
  // Technical content cap per brand (not a quota): empty = `TARGET_LIMITS.maxContenusPerBrand`.
  const [maxContenus, setMaxContenus] = useState('');
  // « Load test » (settings.engineStub): offered only where the API says the env allows it (dev).
  const [engineStubAvailable, setEngineStubAvailable] = useState(false);
  const [engineStub, setEngineStub] = useState(false);
  const maxContenusN = maxContenus.trim() === '' ? null : Number(maxContenus);
  const maxContenusInvalid =
    maxContenusN != null &&
    (!Number.isInteger(maxContenusN) || maxContenusN < 1 || maxContenusN > TARGET_LIMITS.maxContenusPerBrandCeiling);
  const [contractAmountEuros, setContractAmountEuros] = useState('');
  const [contractInterval, setContractInterval] = useState<'month' | 'year'>('month');
  const [contractNotes, setContractNotes] = useState('');
  const [extendTrialDate, setExtendTrialDate] = useState('');
  const [trialTier, setTrialTier] = useState<Tier>('basic');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);

  const load = () => {
    if (!tenantId) return;
    setLoading(true);
    adminService
      .getTenant(tenantId)
      .then(({ tenant: t, users: u, engineStubAvailable: stubAvailable }) => {
        setTenant(t);
        setUsers(u);
        setEngineStubAvailable(!!stubAvailable);
        if (t.tier === 'agency') {
          adminService.getCredits(t.tenantId).then(setCreditBalance).catch(() => undefined);
        }
      })
      .catch((err) => setError(err.message || t('admin.detail.loadError')))
      .finally(() => setLoading(false));
  };

  useEffect(load, [tenantId]);

  const resolvedFlags = useMemo(
    () => (tenant ? effectiveFeatureFlags(tenant.tier, tenant.settings.featureFlags) : {}),
    [tenant],
  );
  const tierDefaults = useMemo(() => (tenant ? TIER_LIMITS[tenant.tier] : undefined), [tenant]);
  const overrides = tenant?.settings.quotaOverrides || {};

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  if (!tenant) return <Alert severity="error">{error || t('admin.detail.notFound')}</Alert>;

  const owner = users.find((u) => u.role === 'owner') || users[0];
  const isAgency = tenant.tier === 'agency';
  const isEnterprise = tenant.tier === 'enterprise';
  // Agency XS = the only agency plan with a brand cap. Recognised by its credit
  // allocation (the plan key itself isn't stored on the tenant).
  const isXsAgency =
    isAgency && isAgencyXsAllocation(tenant.settings.agencyCredits?.monthlyAllocation ?? -1);
  const isStripe = tenant.billingMode === 'stripe';
  // An expired block_on_expiry trial keeps billingStatus='trialing' (nothing flips
  // it — the block is driven by the isTrialExpired predicate). Distinguish a live
  // trial from an ended one so the status chip + the "Terminer l'essai" button
  // reflect reality.
  const trialExpired = isTrialExpired(tenant, Date.now());
  const trialLive = tenant.billingStatus === 'trialing' && !trialExpired;
  const isTrialing = tenant.billingStatus === 'trialing';
  // "Change trial tier" only for basic/pro/elite — agency (credit-trial) and
  // enterprise (contract metadata) are excluded, mirrors TRIAL_ELIGIBLE_TIERS.
  const canChangeTrialTier = isTrialing && TRIAL_ELIGIBLE_TIERS.includes(tenant.tier);

  const closeDialog = () => {
    setDialog(null);
    setCheckoutUrl(null);
  };

  const run = async (fn: () => Promise<void>, successMsg?: string) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      if (successMsg) setMessage(successMsg);
    } catch (err: any) {
      setError(err.message || t('admin.detail.actionFailed'));
    } finally {
      setBusy(false);
    }
  };

  // --- Action handlers ---
  const doAdjustCredits = () =>
    run(async () => {
      const bal = await adminService.adjustCredits(tenant.tenantId, Number(adjustAmount), adjustReason.trim());
      setCreditBalance(bal);
      setAdjustAmount('');
      setAdjustReason('');
      closeDialog();
    }, t('admin.detail.msgCreditsAdjusted'));

  const doSetStatus = (status: BillingStatus, msg: string) =>
    run(async () => {
      await adminService.setBillingStatus(tenant.tenantId, status);
      closeDialog();
      load();
    }, msg);

  const doEndTrial = () =>
    run(async () => {
      await adminService.endTrial(tenant.tenantId);
      closeDialog();
      load();
    }, t('admin.detail.msgTrialEnded'));

  const doExtendTrial = () =>
    run(async () => {
      await adminService.extendTrial(tenant.tenantId, new Date(extendTrialDate).toISOString());
      closeDialog();
      load();
    }, t('admin.detail.msgTrialExtended'));

  const doSetTrialTier = () =>
    run(async () => {
      await adminService.setTrialTier(tenant.tenantId, trialTier);
      closeDialog();
      load();
    }, t('admin.detail.msgTrialTierChanged'));

  const doResetPassword = () =>
    run(async () => {
      const { to } = await adminService.resetPassword(tenant.tenantId, resetEmail || undefined);
      closeDialog();
      setMessage(t('admin.detail.msgPasswordReset', { to }));
    });

  const doResendWelcome = () =>
    run(async () => {
      const { to } = await adminService.resendWelcomeEmail(tenant.tenantId);
      setMessage(t('admin.detail.msgWelcomeResent', { to }));
    });

  const doSaveQuotas = () =>
    run(async () => {
      const qo: QuotaOverrides = {};
      for (const k of QUOTA_KEYS) {
        const raw = quotaOverrides[k];
        if (raw != null && raw !== '') {
          const n = Number(raw);
          if (!Number.isNaN(n)) (qo as Record<string, number>)[k] = n;
        }
      }
      for (const k of BOOLEAN_QUOTA_KEYS) {
        const raw = quotaOverrides[k];
        if (raw === 'true' || raw === 'false') (qo as Record<string, boolean>)[k] = raw === 'true';
      }
      await adminService.updateTenant(tenant.tenantId, {
        settings: {
          quotaOverrides: Object.keys(qo).length ? qo : undefined,
          featureFlags,
          enabledEngines: enabledEngines.length ? enabledEngines : undefined,
          // `null` (not undefined) when emptied, so a previous override is cleared.
          targetLimitOverrides: { maxContenusPerBrand: maxContenusN },
          ...(engineStubAvailable ? { engineStub } : {}),
        },
      });
      closeDialog();
      load();
    }, t('admin.detail.msgQuotasSaved'));

  const doSaveContract = () =>
    run(async () => {
      await adminService.updateTenant(tenant.tenantId, {
        contractAmountCents: contractAmountEuros ? Math.round(Number(contractAmountEuros) * 100) : undefined,
        contractInterval,
        contractNotes: contractNotes || undefined,
      });
      closeDialog();
      load();
    }, t('admin.detail.msgContractSaved'));

  const doGenerateCheckout = () =>
    run(async () => {
      const origin = window.location.origin;
      const { url } = await adminService.createCheckoutSession(tenant.tenantId, {
        successUrl: `${origin}/admin/tenants/${tenant.tenantId}?stripe=success`,
        cancelUrl: `${origin}/admin/tenants/${tenant.tenantId}?stripe=cancelled`,
      });
      setCheckoutUrl(url);
    });

  const doDelete = () =>
    run(async () => {
      const res = await adminService.deleteTenant(tenant.tenantId, deleteConfirmation);
      // Partial purge: the tenant row is kept on purpose so the purge can be re-run.
      if (!res.deleted) throw new Error(t('admin.detail.deletePartial', { errors: res.errors.join(' | ') }));
      navigate('/tenants');
    });

  // Open helpers that seed action-local state from the current tenant.
  const openQuotas = () => {
    const q: Record<string, string> = {};
    for (const k of QUOTA_KEYS) {
      const v = (overrides as any)[k];
      if (typeof v === 'number') q[k] = String(v);
    }
    for (const k of BOOLEAN_QUOTA_KEYS) {
      const v = (overrides as any)[k];
      if (typeof v === 'boolean') q[k] = String(v);
    }
    setQuotaOverrides(q);
    setFeatureFlags({ ...tenant.settings.featureFlags });
    setEnabledEngines(
      tenant.settings.enabledEngines && tenant.settings.enabledEngines.length > 0
        ? tenant.settings.enabledEngines
        : DEFAULT_ENGINE_IDS,
    );
    const mc = tenant.settings.targetLimitOverrides?.maxContenusPerBrand;
    setMaxContenus(typeof mc === 'number' ? String(mc) : '');
    setEngineStub(tenant.settings.engineStub === true);
    setDialog('quotas');
  };
  const openContract = () => {
    setContractAmountEuros(tenant.contractAmountCents != null ? String(tenant.contractAmountCents / 100) : '');
    setContractInterval(tenant.contractInterval || 'month');
    setContractNotes(tenant.contractNotes || '');
    setDialog('contract');
  };
  const openExtendTrial = () => {
    // Default to 14 days out from today (or from the current trialEndsAt if
    // it's further out than today, e.g. extending a still-live trial).
    const base = Math.max(Date.now(), tenant.trialEndsAt ? new Date(tenant.trialEndsAt).getTime() : 0);
    setExtendTrialDate(new Date(base + 14 * 86400_000).toISOString().slice(0, 10));
    setDialog('extend-trial');
  };
  const openTrialTier = () => {
    setTrialTier(TRIAL_ELIGIBLE_TIERS.includes(tenant.tier) ? tenant.tier : 'basic');
    setDialog('trial-tier');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/tenants')} size="small">
          {t('admin.detail.back')}
        </Button>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        <Typography variant="h5" fontWeight={700}>{tenant.name}</Typography>
        <Chip label={tenant.tier} size="small" sx={{ textTransform: 'capitalize' }} />
        <Chip
          label={trialExpired ? t('admin.detail.trialEnded') : tenant.billingStatus}
          size="small"
          sx={{
            bgcolor: `${trialExpired ? BILLING_COLORS.suspended : BILLING_COLORS[tenant.billingStatus]}20`,
            color: trialExpired ? BILLING_COLORS.suspended : BILLING_COLORS[tenant.billingStatus],
            fontWeight: 600,
          }}
        />
        <Typography variant="body2" color="text.secondary">{tenant.ownerEmail || owner?.email}</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message}</Alert>}
      {tenant.settings.engineStub && <Alert severity="warning" sx={{ mb: 2 }}>{t('admin.detail.engineStubActive')}</Alert>}

      <Stack spacing={3}>
        {/* Quick actions */}
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>{t('admin.detail.actionsTitle')}</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {isAgency && (
                <Button size="small" variant="outlined" startIcon={<PaymentsIcon />} onClick={() => setDialog('credits')}>
                  {t('admin.detail.actionAdjustCredits')}
                </Button>
              )}
              {isAdmin && (tenant.billingStatus === 'suspended' ? (
                <Button size="small" variant="outlined" color="success" startIcon={<PlayArrowIcon />} onClick={() => setDialog('reactivate')}>
                  {t('admin.detail.actionReactivate')}
                </Button>
              ) : (
                <Button size="small" variant="outlined" color="warning" startIcon={<BlockIcon />} onClick={() => setDialog('suspend')}>
                  {t('admin.detail.actionSuspend')}
                </Button>
              ))}
              {isAdmin && trialLive && (
                <Button size="small" variant="outlined" color="warning" startIcon={<TimerOffIcon />} onClick={() => setDialog('end-trial')}>
                  {t('admin.detail.actionEndTrial')}
                </Button>
              )}
              {isTrialing && (
                <Button size="small" variant="outlined" startIcon={<UpdateIcon />} onClick={openExtendTrial}>
                  {trialExpired ? t('admin.detail.actionReactivateTrial') : t('admin.detail.actionExtendTrial')}
                </Button>
              )}
              {isAdmin && canChangeTrialTier && (
                <Button size="small" variant="outlined" startIcon={<SwapHorizIcon />} onClick={openTrialTier}>
                  {t('admin.detail.actionTrialTier')}
                </Button>
              )}
              <Button size="small" variant="outlined" startIcon={<LockResetIcon />} onClick={() => { setResetEmail(owner?.email || ''); setDialog('reset'); }}>
                {t('admin.detail.actionResetPassword')}
              </Button>
              {isAdmin && (
                <Button size="small" variant="outlined" startIcon={<EmailIcon />} onClick={doResendWelcome} disabled={busy}>
                  {t('admin.detail.actionResendWelcome')}
                </Button>
              )}
              {isAdmin && isStripe && (
                <Button size="small" variant="outlined" startIcon={<LaunchIcon />} onClick={() => { setCheckoutUrl(null); doGenerateCheckout(); }} disabled={busy || !tenant.contractAmountCents}>
                  {t('admin.detail.actionGenerateCheckout')}
                </Button>
              )}
              {/* Quotas + feature flags: admin only */}
              {isAdmin && (
                <Button size="small" variant="outlined" startIcon={<TuneIcon />} onClick={openQuotas}>
                  {t('admin.detail.actionQuotas')}
                </Button>
              )}
              {isAdmin && (isEnterprise || isAgency || tenant.billingMode === 'manual') && (
                <Button size="small" variant="outlined" startIcon={<ReceiptLongIcon />} onClick={openContract}>
                  {t('admin.detail.actionContract')}
                </Button>
              )}
              <Box sx={{ flex: 1 }} />
              {isAdmin && (
                <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => { setDeleteConfirmation(''); setDialog('delete'); }}>
                  {t('admin.detail.actionDelete')}
                </Button>
              )}
            </Stack>
            {isStripe && checkoutUrl && (
              <Alert severity="success" sx={{ mt: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField size="small" value={checkoutUrl} fullWidth InputProps={{ readOnly: true }} sx={{ minWidth: 360 }} />
                  <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => navigator.clipboard.writeText(checkoutUrl)}>{t('admin.detail.copy')}</Button>
                  <Button size="small" startIcon={<LaunchIcon />} component="a" href={checkoutUrl} target="_blank" rel="noopener">{t('admin.detail.open')}</Button>
                </Stack>
              </Alert>
            )}
            {isStripe && !tenant.contractAmountCents && (
              <Alert severity="warning" sx={{ mt: 2 }}>{t('admin.detail.contractRequiredForCheckout')}</Alert>
            )}
          </CardContent>
        </Card>

        {/* Info */}
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>{t('admin.detail.infoTitle')}</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, md: 3 }}><Field label={t('admin.detail.fieldDomain')} value={tenant.domain} /></Grid>
              <Grid size={{ xs: 6, md: 3 }}><Field label={t('admin.detail.fieldBillingMode')} value={tenant.billingMode} /></Grid>
              <Grid size={{ xs: 6, md: 3 }}><Field label={t('admin.detail.fieldCreatedAt')} value={new Date(tenant.createdAt).toLocaleDateString()} /></Grid>
              {tenant.trialEndsAt && (
                <Grid size={{ xs: 6, md: 3 }}><Field label={t('admin.detail.fieldTrialEnd')} value={new Date(tenant.trialEndsAt).toLocaleDateString()} /></Grid>
              )}
              {tenant.stripeCustomerId && (
                <Grid size={{ xs: 12, md: 6 }}><Field label={t('admin.detail.fieldStripeCustomer')} value={tenant.stripeCustomerId} /></Grid>
              )}
              {tenant.contractAmountCents != null && (
                <Grid size={{ xs: 6, md: 3 }}><Field label={t('admin.detail.fieldContract')} value={`${(tenant.contractAmountCents / 100).toFixed(0)} € HT/${tenant.contractInterval || 'month'}`} /></Grid>
              )}
            </Grid>
            {tenant.contractNotes && (
              <Box sx={{ mt: 2 }}><Field label={t('admin.detail.fieldContractNotes')} value={tenant.contractNotes} /></Box>
            )}
          </CardContent>
        </Card>

        {/* Agency credits */}
        {isAgency && (
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>{t('admin.detail.creditsTitle')}</Typography>
              {creditBalance ? (
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6, md: 3 }}><Field label={t('admin.detail.creditsSubscription')} value={creditBalance.subscriptionCredits} /></Grid>
                  <Grid size={{ xs: 6, md: 3 }}><Field label={t('admin.detail.creditsTopup')} value={creditBalance.topupCredits} /></Grid>
                  <Grid size={{ xs: 6, md: 3 }}><Field label={t('admin.detail.creditsReserved')} value={creditBalance.reserved} /></Grid>
                  <Grid size={{ xs: 6, md: 3 }}><Field label={t('admin.detail.creditsAvailable')} value={<strong>{creditBalance.available}</strong>} /></Grid>
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">—</Typography>
              )}
              <Box sx={{ mt: 2 }}>
                <Field
                  label={t('admin.detail.creditsMonthlyAllocation')}
                  value={tenant.settings.agencyCredits?.monthlyAllocation ?? '—'}
                />
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Resolved limits */}
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>{t('admin.detail.limitsTitle')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('admin.detail.limitsHelp')}
            </Typography>
            <Grid container spacing={2}>
              {QUOTA_KEYS.map((k) => {
                const ov = (overrides as any)[k];
                const def = (tierDefaults as any)?.[k];
                const eff = ov ?? def;
                return (
                  <Grid size={{ xs: 6, sm: 4, md: 3 }} key={k}>
                    <Typography variant="caption" color="text.secondary">{k}</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {eff === -1 ? '∞' : eff ?? '—'}
                      {ov != null && <Chip label="override" size="small" sx={{ ml: 0.5, height: 16, fontSize: 10 }} />}
                    </Typography>
                  </Grid>
                );
              })}
              {BOOLEAN_QUOTA_KEYS.map((k) => {
                const ov = (overrides as any)[k];
                const def = (tierDefaults as any)?.[k];
                const eff: boolean = ov ?? def ?? false;
                return (
                  <Grid size={{ xs: 6, sm: 4, md: 3 }} key={k}>
                    <Typography variant="caption" color="text.secondary">{k}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip
                        label={eff ? t('admin.detail.boolOn') : t('admin.detail.boolOff')}
                        size="small"
                        color={eff ? 'success' : 'default'}
                        variant={eff ? 'filled' : 'outlined'}
                      />
                      {ov != null && <Chip label="override" size="small" sx={{ height: 16, fontSize: 10 }} />}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>

        {/* Feature flags */}
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              {isAgency ? t('admin.detail.featuresTitleAgency') : t('admin.detail.featuresTitle')}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {FLAG_LABELS.map(({ key, labelKey }) => (
                <Chip
                  key={key}
                  label={t(labelKey)}
                  size="small"
                  color={resolvedFlags[key] ? 'success' : 'default'}
                  variant={resolvedFlags[key] ? 'filled' : 'outlined'}
                />
              ))}
              {isAgency && AGENCY_ONLY_FLAGS.map(({ key, labelKey }) => (
                <Chip
                  key={key}
                  label={t(labelKey)}
                  size="small"
                  color={resolvedFlags[key] ? 'success' : 'default'}
                  variant={resolvedFlags[key] ? 'filled' : 'outlined'}
                />
              ))}
            </Stack>
          </CardContent>
        </Card>

        {/* Users */}
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>{t('admin.detail.usersTitle', { count: users.length })}</Typography>
            <Stack spacing={1}>
              {users.map((u) => (
                <Box key={u.userId} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{u.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {u.email} · {u.role}{u.cognitoSub.startsWith('pending:') ? t('admin.detail.userPendingSuffix') : ''}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    startIcon={<LockResetIcon />}
                    onClick={() => { setResetEmail(u.email); setDialog('reset'); }}
                  >
                    {t('admin.detail.resetPasswordShort')}
                  </Button>
                </Box>
              ))}
              {users.length === 0 && <Typography variant="body2" color="text.secondary">{t('admin.detail.noUsers')}</Typography>}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      {/* ---- Action dialogs ---- */}

      {/* Adjust credits */}
      <Dialog open={dialog === 'credits'} onClose={() => !busy && closeDialog()} maxWidth="sm" fullWidth>
        <DialogTitle>{t('admin.detail.dlgCreditsTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('admin.detail.dlgCreditsText')}
          </DialogContentText>
          <Stack spacing={2}>
            <TextField label={t('admin.detail.dlgCreditsAmount')} type="number" size="small" value={adjustAmount} onChange={(e) => setAdjustAmount(e.target.value)} autoFocus />
            <TextField label={t('admin.detail.dlgCreditsReason')} size="small" value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={busy}>{t('admin.detail.cancel')}</Button>
          <Button variant="contained" onClick={doAdjustCredits} disabled={busy || !adjustAmount || !adjustReason.trim()}>
            {t('admin.detail.apply')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Suspend */}
      <Dialog open={dialog === 'suspend'} onClose={() => !busy && closeDialog()} maxWidth="sm" fullWidth>
        <DialogTitle>{t('admin.detail.dlgSuspendTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Trans i18nKey="admin.detail.dlgSuspendText" components={{ b: <strong /> }} />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={busy}>{t('admin.detail.cancel')}</Button>
          <Button variant="contained" color="warning" onClick={() => doSetStatus('suspended', t('admin.detail.msgSuspended'))} disabled={busy}>
            {t('admin.detail.suspend')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* End trial */}
      <Dialog open={dialog === 'end-trial'} onClose={() => !busy && closeDialog()} maxWidth="sm" fullWidth>
        <DialogTitle>{t('admin.detail.dlgEndTrialTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Trans i18nKey="admin.detail.dlgEndTrialText" components={{ b: <strong /> }} />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={busy}>{t('admin.detail.cancel')}</Button>
          <Button variant="contained" color="warning" onClick={doEndTrial} disabled={busy}>
            {t('admin.detail.dlgEndTrialConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Extend / reactivate trial — same action either way: push trialEndsAt
          into the future. Expiry never flips billingStatus, so a trial that has
          already ended and one still running are handled identically. */}
      <Dialog open={dialog === 'extend-trial'} onClose={() => !busy && closeDialog()} maxWidth="sm" fullWidth>
        <DialogTitle>
          {trialExpired ? t('admin.detail.dlgReactivateTrialTitle') : t('admin.detail.dlgExtendTrialTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {trialExpired ? t('admin.detail.dlgReactivateTrialText') : t('admin.detail.dlgExtendTrialText')}
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            size="small"
            type="date"
            label={t('admin.detail.dlgExtendTrialDate')}
            value={extendTrialDate}
            onChange={(e) => setExtendTrialDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={busy}>{t('admin.detail.cancel')}</Button>
          <Button variant="contained" onClick={doExtendTrial} disabled={busy || !extendTrialDate}>
            {trialExpired ? t('admin.detail.dlgReactivateTrialConfirm') : t('admin.detail.dlgExtendTrialConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change trial tier — trial-only (billingStatus==='trialing'), basic/pro/elite
          only. Safe because a no_card trial never creates a Stripe customer or
          subscription, so there's nothing to desync. */}
      <Dialog open={dialog === 'trial-tier'} onClose={() => !busy && closeDialog()} maxWidth="sm" fullWidth>
        <DialogTitle>{t('admin.detail.dlgTrialTierTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('admin.detail.dlgTrialTierText')}
          </DialogContentText>
          <FormControl fullWidth size="small">
            <InputLabel>{t('admin.detail.dlgTrialTierLabel')}</InputLabel>
            <Select
              label={t('admin.detail.dlgTrialTierLabel')}
              value={trialTier}
              onChange={(e) => setTrialTier(e.target.value as Tier)}
            >
              {TRIAL_ELIGIBLE_TIERS.map((tr) => (
                <MenuItem key={tr} value={tr} sx={{ textTransform: 'capitalize' }}>{tr}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={busy}>{t('admin.detail.cancel')}</Button>
          <Button variant="contained" onClick={doSetTrialTier} disabled={busy || trialTier === tenant.tier}>
            {t('admin.detail.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reactivate */}
      <Dialog open={dialog === 'reactivate'} onClose={() => !busy && closeDialog()} maxWidth="sm" fullWidth>
        <DialogTitle>{t('admin.detail.dlgReactivateTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Trans i18nKey="admin.detail.dlgReactivateText" components={{ b: <strong /> }} />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={busy}>{t('admin.detail.cancel')}</Button>
          <Button variant="contained" color="success" onClick={() => doSetStatus('active', t('admin.detail.msgReactivated'))} disabled={busy}>
            {t('admin.detail.reactivate')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset password */}
      <Dialog open={dialog === 'reset'} onClose={() => !busy && closeDialog()} maxWidth="sm" fullWidth>
        <DialogTitle>{t('admin.detail.dlgResetTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('admin.detail.dlgResetText')}
          </DialogContentText>
          <FormControl fullWidth size="small">
            <InputLabel>{t('admin.detail.dlgResetUser')}</InputLabel>
            <Select label={t('admin.detail.dlgResetUser')} value={resetEmail} onChange={(e) => setResetEmail(e.target.value)}>
              {users.map((u) => (
                <MenuItem key={u.userId} value={u.email}>
                  {u.name} — {u.email} ({u.role})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={busy}>{t('admin.detail.cancel')}</Button>
          <Button variant="contained" onClick={doResetPassword} disabled={busy || !resetEmail.trim()}>
            {t('admin.detail.send')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit quotas + flags (enterprise) */}
      <Dialog open={dialog === 'quotas'} onClose={() => !busy && closeDialog()} maxWidth="md" fullWidth>
        <DialogTitle>{t('admin.detail.dlgQuotasTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('admin.detail.dlgQuotasHelp')}
          </Typography>
          <Grid container spacing={2}>
            {QUOTA_KEYS.map((k) => (
              <Grid size={{ xs: 6, sm: 4, md: 3 }} key={k}>
                <TextField
                  label={k}
                  type="number"
                  size="small"
                  fullWidth
                  value={quotaOverrides[k] || ''}
                  onChange={(e) => setQuotaOverrides({ ...quotaOverrides, [k]: e.target.value })}
                  placeholder={String((tierDefaults as any)?.[k] ?? '')}
                  helperText={
                    // Agency XS is the one agency plan with a brand cap, and it is
                    // posed BY HAND here (never auto-set, so it stays negotiable).
                    // Surface the canonical number rather than the tier default ∞.
                    k === 'brands' && isXsAgency
                      ? t('admin.detail.xsBrandCap', { limit: AGENCY_XS_MAX_BRANDS })
                      : t('admin.detail.defaultValue', { value: (tierDefaults as any)?.[k] ?? '—' })
                  }
                />
              </Grid>
            ))}
            {BOOLEAN_QUOTA_KEYS.map((k) => (
              <Grid size={{ xs: 6, sm: 4, md: 3 }} key={k}>
                <FormControl fullWidth size="small">
                  <InputLabel>{k}</InputLabel>
                  <Select
                    label={k}
                    value={quotaOverrides[k] || ''}
                    onChange={(e) => setQuotaOverrides({ ...quotaOverrides, [k]: e.target.value })}
                  >
                    <MenuItem value="">
                      {t('admin.detail.defaultValue', {
                        value: (tierDefaults as any)?.[k] ? t('admin.detail.boolOn') : t('admin.detail.boolOff'),
                      })}
                    </MenuItem>
                    <MenuItem value="true">{t('admin.detail.boolOn')}</MenuItem>
                    <MenuItem value="false">{t('admin.detail.boolOff')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            ))}
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Stack>
            {FLAG_LABELS.map(({ key, labelKey }) => (
              <FormControlLabel
                key={key}
                control={<Switch checked={!!featureFlags[key]} onChange={(e) => setFeatureFlags({ ...featureFlags, [key]: e.target.checked })} />}
                label={t(labelKey)}
              />
            ))}
            {isAgency && AGENCY_ONLY_FLAGS.map(({ key, labelKey }) => (
              <FormControlLabel
                key={key}
                control={
                  <Switch
                    checked={!!featureFlags[key]}
                    onChange={(e) =>
                      setFeatureFlags({ ...featureFlags, [key]: e.target.checked })
                    }
                  />
                }
                label={t(labelKey)}
              />
            ))}
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>{t('admin.detail.enginesTitle')}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t('admin.detail.enginesHelp')}
          </Typography>
          <Stack sx={{ mt: 1 }}>
            {ENGINE_OPTIONS.map((e) => (
              <FormControlLabel
                key={e.id}
                control={
                  <Switch
                    checked={enabledEngines.includes(e.id)}
                    onChange={(ev) =>
                      setEnabledEngines((prev) =>
                        ev.target.checked ? [...prev, e.id] : prev.filter((x) => x !== e.id),
                      )
                    }
                  />
                }
                label={e.label}
              />
            ))}
          </Stack>
          <Divider sx={{ my: 2 }} />
          <TextField
            label={t('admin.detail.maxContenusLabel')}
            type="number"
            size="small"
            fullWidth
            value={maxContenus}
            onChange={(e) => setMaxContenus(e.target.value)}
            placeholder={TARGET_LIMITS.maxContenusPerBrand.toLocaleString()}
            inputProps={{ min: 1, max: TARGET_LIMITS.maxContenusPerBrandCeiling, step: 1 }}
            error={maxContenusInvalid}
            helperText={t('admin.detail.maxContenusHelp', {
              def: TARGET_LIMITS.maxContenusPerBrand.toLocaleString(),
              max: TARGET_LIMITS.maxContenusPerBrandCeiling.toLocaleString(),
            })}
          />
          {engineStubAvailable && (
            <>
              <Divider sx={{ my: 2 }} />
              <FormControlLabel
                control={<Switch checked={engineStub} onChange={(e) => setEngineStub(e.target.checked)} />}
                label={t('admin.detail.engineStubLabel')}
              />
              <Typography variant="caption" color="text.secondary" component="div">
                {t('admin.detail.engineStubHelp')}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={busy}>{t('admin.detail.cancel')}</Button>
          <Button variant="contained" onClick={doSaveQuotas} disabled={busy || maxContenusInvalid}>{t('admin.detail.save')}</Button>
        </DialogActions>
      </Dialog>

      {/* Edit contract */}
      <Dialog open={dialog === 'contract'} onClose={() => !busy && closeDialog()} maxWidth="sm" fullWidth>
        <DialogTitle>{t('admin.detail.dlgContractTitle')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label={t('admin.detail.negotiatedAmount')} type="number" size="small" fullWidth value={contractAmountEuros} onChange={(e) => setContractAmountEuros(e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('admin.detail.interval')}</InputLabel>
                <Select label={t('admin.detail.interval')} value={contractInterval} onChange={(e) => setContractInterval(e.target.value as 'month' | 'year')}>
                  <MenuItem value="month">{t('admin.detail.intervalMonthly')}</MenuItem>
                  <MenuItem value="year">{t('admin.detail.intervalYearly')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label={t('admin.detail.contractNotes')} size="small" fullWidth multiline minRows={2} value={contractNotes} onChange={(e) => setContractNotes(e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={busy}>{t('admin.detail.cancel')}</Button>
          <Button variant="contained" onClick={doSaveContract} disabled={busy}>{t('admin.detail.save')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete */}
      <Dialog open={dialog === 'delete'} onClose={() => !busy && closeDialog()} maxWidth="sm" fullWidth>
        <DialogTitle>{t('admin.detail.dlgDeleteTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            <Trans i18nKey="admin.detail.dlgDeleteText" values={{ name: tenant.name }} components={{ b: <strong />, br: <br /> }} />
          </DialogContentText>
          <TextField autoFocus fullWidth size="small" label={t('admin.detail.dlgDeleteLabel')} value={deleteConfirmation} onChange={(e) => setDeleteConfirmation(e.target.value)} disabled={busy} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={busy}>{t('admin.detail.cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={doDelete}
            disabled={busy || deleteConfirmation.trim().toLowerCase() !== tenant.name.trim().toLowerCase()}
          >
            {busy ? t('admin.detail.deleting') : t('admin.detail.deleteConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
